import type { TrackedTranslation } from '@ps/web-core-intl';
import { useTrackers } from '@ps/web-shared-analytics';
import { useTrackingLabel } from '@ps/web-shared-components';
import type { ClassNameProp } from '@ps/web-shared-components';
import type { ValidatorTranslation } from '@ps/web-shared-validators';
import { required } from '@ps/web-shared-validators';
import classNames from 'clsx';
import { isFunction } from 'lodash';
import React, { useMemo, useRef, useState } from 'react';
import type { FieldProps, FieldRenderProps } from 'react-final-form';
import { Field, useForm } from 'react-final-form';
import type { ListChildComponentProps } from 'react-window';
import { FixedSizeList } from 'react-window';

import type { Country } from '@swf/core/enums';
import useValidator from '@swf/core/hooks/useValidator';

import Modal from '../Modal/Modal';
import SearchInput from '../SearchInput/SearchInput';

import { container, disabledInput, searchInput, searchInputField, view } from './Select.scss';

interface Item {
  text: string;
  value: string;
  onClick?: () => void;
}

export interface Props
  extends FieldProps<string, FieldRenderProps<string, HTMLElement>, HTMLSelectElement>,
    Partial<ClassNameProp> {
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item: React.FC<any>;
  options: Item[];
  submit?: boolean;
  searchLabel?: TrackedTranslation;
  label?: TrackedTranslation;
  onSelectedItemChange?: () => void;
  title?: TrackedTranslation;
  validatorTranslation?: ValidatorTranslation;
  closeModal?: Country[];
}

interface SelectItemProps extends ListChildComponentProps {
  data: {
    displayedOptions: Item[];
    item: React.ComponentType<Item>;
    onClick: (optionValue: string) => void;
  };
}

const SelectItem: React.FC<SelectItemProps> = React.memo(({ index, style, data }) => {
  const option = data.displayedOptions[index];
  return (
    <div style={style}>
      <data.item {...option} onClick={() => data.onClick(option.value)} />
    </div>
  );
});

const itemHeight = 56;
const matchAny = new RegExp('.');
const searchThreshold = 8;

const Select: React.FC<Props> = ({
  name,
  className,
  options,
  item,
  title,
  submit,
  searchLabel,
  children,
  label,
  onSelectedItemChange,
  onClick,
  disabled,
  validatorTranslation,
  closeModal,
  ...props
}) => {
  const [panelVisible, setPanelVisible] = useState(false);
  const searchInputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const form = useForm();
  const [searchQuery, setSearchQuery] = useState<RegExp>(matchAny);
  const { inputFilled } = useTrackers();
  const fieldName = useTrackingLabel(label);

  const focusSearchInput = () => {
    const { current } = searchInputRef;
    current && current?.focus();
  };

  const hidePanel = (renderProps: FieldRenderProps<string, HTMLSelectElement>) => {
    setPanelVisible(false);
    renderProps.input.onBlur();
  };

  const [validator] = useValidator(required(validatorTranslation));

  const displayedOptions = useMemo(
    () =>
      options.filter(({ text, value }) => text?.match(searchQuery) || value?.match(searchQuery)),
    [options, searchQuery]
  );

  return (
    <Field name={name} {...(validatorTranslation ? { validate: validator } : {})} {...props}>
      {(renderProps) => (
        <>
          <div
            className={classNames(className, { [disabledInput]: disabled })}
            onClick={() => {
              if (disabled) return;
              renderProps.input.onFocus();
              setPanelVisible(true);
              setSearchQuery(matchAny);
            }}
          >
            {isFunction(children) ? children(renderProps) : children}
          </div>
          <Modal
            visible={panelVisible}
            title={title}
            onMount={focusSearchInput}
            onClose={() => hidePanel(renderProps)}
          >
            <div className={classNames(container)}>
              {searchLabel && options.length >= searchThreshold && (
                <div className={searchInput}>
                  <SearchInput
                    id={name}
                    label={searchLabel}
                    onChange={({ target: { value } }) =>
                      setSearchQuery(value ? new RegExp(`(?:^|\\s)${value}`, 'gi') : matchAny)
                    }
                    className={searchInputField}
                    inputRef={searchInputRef}
                  />
                </div>
              )}
              <FixedSizeList
                className={view}
                itemCount={displayedOptions.length}
                itemSize={itemHeight}
                width="100%"
                height={40 * itemHeight}
                itemData={{
                  displayedOptions,
                  item,
                  onClick: (strInput: string) => {
                    renderProps.input.value !== strInput && onSelectedItemChange?.();
                    renderProps.input.onChange(strInput);
                    onClick?.(strInput);
                    (!closeModal || closeModal?.includes(strInput as Country)) &&
                      hidePanel(renderProps);
                    inputFilled({ fieldName, fieldType: 'dropdown', strInput });
                    submit && form.submit();
                  }
                }}
                itemKey={(index, data) =>
                  data.displayedOptions[index].value + data.displayedOptions[index].text
                }
              >
                {SelectItem}
              </FixedSizeList>
            </div>
          </Modal>
        </>
      )}
    </Field>
  );
};

export default Select;
